import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSidebar } from '../context/SidebarContext';
import { useTeams } from '../context/TeamsContext';
import PersonalizationModal from './PersonalizationModal';
import { CachedImage } from './CachedImage';

interface HeaderProps {
  hideTitle?: boolean;
  currentAgent?: {
    id: string;
    title: string;
    description: string;
    img_url: string;
  } | null;
}

const Header: React.FC<HeaderProps> = ({ hideTitle = true, currentAgent = null }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const { colors, theme, toggleTheme } = useTheme();
  const { isSidebarOpen } = useSidebar();
  const { isInTeams, theme: teamsTheme } = useTeams();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isReleaseNotesOpen, setIsReleaseNotesOpen] = useState(false);
  const [releaseNotes, setReleaseNotes] = useState<any>(null);
  const [isPersonalizationOpen, setIsPersonalizationOpen] = useState(false);
  const [hasNewReleaseNotes, setHasNewReleaseNotes] = useState(false);
  const [persona, setPersona] = useState<string>(() => {
    return localStorage.getItem('chatPersona') || 'default';
  });
  const menuRef = useRef<HTMLDivElement>(null);
  
  // App title can be configured through environment variable
  const appTitle = process.env.REACT_APP_APP_TITLE || 'Chat VNT 2.0';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load release notes from JSON file
  useEffect(() => {
    const loadReleaseNotes = async () => {
      try {
        const response = await fetch('/release-notes.json');
        const data = await response.json();
        setReleaseNotes(data);

        const latestRelease = data.releases?.find((r: any) => r.isLatest);
        if (latestRelease) {
          const lastSeenVersion = localStorage.getItem('lastSeenReleaseVersion');
          if (lastSeenVersion !== latestRelease.version) {
            setHasNewReleaseNotes(true);
          }
        }
      } catch (error) {
        console.error('Failed to load release notes:', error);
      }
    };

    loadReleaseNotes();
  }, []);

  const handlePersonaChange = (newPersona: string) => {
    setPersona(newPersona);
    localStorage.setItem('chatPersona', newPersona);
  };

  const handleOpenReleaseNotes = () => {
    setIsReleaseNotesOpen(true);
    setIsMenuOpen(false);
    setHasNewReleaseNotes(false);

    const latestRelease = releaseNotes?.releases?.find((r: any) => r.isLatest);
    if (latestRelease) {
      localStorage.setItem('lastSeenReleaseVersion', latestRelease.version);
    }
  };

  const personas = [
    { id: 'default', name: 'Default', icon: '💬' },
    { id: 'hr', name: 'HR', icon: '👥' },
    { id: 'sales', name: 'Sales', icon: '💼' },
    { id: 'operations', name: 'Operations', icon: '⚙️' },
    { id: 'it', name: 'IT', icon: '💻' },
    { id: 'scrum', name: 'Scrum Expert', icon: '🏃' },
    { id: 'direct', name: 'Direct', icon: '🎯' }
  ];

  const currentPersonaData = personas.find(p => p.id === persona);

  return (
    <>
      <style>
        {`
          @keyframes notification-pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.5;
              transform: scale(1.1);
            }
          }
        `}
      </style>
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        {!hideTitle && (
        <h1 style={{
          margin: 0,
          fontSize: '1.25rem',
          fontWeight: '600',
          letterSpacing: '-0.01em',
          color: colors.textPrimary,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            {appTitle}ß
          </Link>
        </h1>
      )}

      {isAuthenticated && (
        <>
          {/* Agent Indicator */}
          {currentAgent && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              backgroundColor: colors.surfaceElevated,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '500',
              color: colors.textPrimary,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}>
              <CachedImage
                src={currentAgent.img_url}
                alt={currentAgent.title}
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  objectFit: 'cover'
                }}
              />
              <span>{currentAgent.title}</span>
            </div>
          )}
          {/* Persona Indicator */}
          {!currentAgent && persona !== 'default' && currentPersonaData && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              backgroundColor: colors.surfaceElevated,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '500',
              color: colors.textPrimary,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}>
              <span style={{ fontSize: '16px', lineHeight: 1 }}>
                {currentPersonaData.icon}
              </span>
              <span>{currentPersonaData.name}</span>
            </div>
          )}
        </>
      )}

      {isAuthenticated && (
        <div style={{ position: 'relative' }} ref={menuRef}>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = theme === 'light' ? '#f1f5f9' : '#475569'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              padding: '8px 12px',
              color: colors.textSecondary,
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {hasNewReleaseNotes && (
              <span style={{
                width: '8px',
                height: '8px',
                backgroundColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'notification-pulse 2s ease-in-out infinite'
              }} />
            )}
            {user?.name || 'User'}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}
            >
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          {isMenuOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: '0',
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              minWidth: '160px',
              zIndex: 1000,
              marginTop: '4px'
            }}>
              {!isInTeams && (
                <button
                  onClick={() => {
                    toggleTheme();
                    setIsMenuOpen(false);
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = colors.buttonHover}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    fontWeight: '500',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '6px',
                    margin: '4px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {theme === 'light' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                  {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                </button>
              )}
              <button
                onClick={() => {
                  setIsPersonalizationOpen(true);
                  setIsMenuOpen(false);
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = colors.buttonHover}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                  color: colors.textPrimary,
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderRadius: '6px',
                  margin: '4px',
                  transition: 'all 0.2s ease'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Personalization
              </button>
              {isInTeams && (
                <div style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  color: colors.textSecondary,
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderRadius: '6px',
                  margin: '4px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.5 3A6.5 6.5 0 0 0 3 9.5V16h3v3h6.5a6.5 6.5 0 0 0 6.5-6.5V9.5A6.5 6.5 0 0 0 12.5 3h-3z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Theme: Teams ({teamsTheme})
                </div>
              )}
              <button
                onClick={handleOpenReleaseNotes}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = colors.buttonHover}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                  color: colors.textPrimary,
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderRadius: '6px',
                  margin: '4px',
                  transition: 'all 0.2s ease'
                }}
              >
                {hasNewReleaseNotes && (
                  <span style={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#3b82f6',
                    borderRadius: '50%',
                    animation: 'notification-pulse 2s ease-in-out infinite'
                  }} />
                )}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Release Notes
              </button>
              <button 
                onClick={() => {
                  logout();
                  setIsMenuOpen(false);
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = colors.buttonHover}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                  color: colors.textPrimary,
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderRadius: '6px',
                  margin: '4px',
                  transition: 'all 0.2s ease'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 8L18 12M18 12L14 16M18 12H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M11 3H7C4.79086 3 3 4.79086 3 7V17C3 19.2091 4.79086 21 7 21H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Release Notes Modal */}
      {isReleaseNotesOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: colors.surface,
            borderRadius: '12px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: colors.textPrimary
              }}>
                Release Notes
              </h2>
              <button
                onClick={() => setIsReleaseNotesOpen(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '6px',
                  color: colors.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = colors.buttonHover}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            {/* Modal Content - Scrollable */}
            <div style={{
              padding: '24px',
              overflowY: 'auto',
              flex: 1
            }}>
              <div style={{ color: colors.textPrimary, lineHeight: '1.6' }}>
                {releaseNotes && releaseNotes.releases ? (
                  <>
                    {releaseNotes.releases.map((release: any, index: number) => (
                      <div key={release.version} style={{ marginBottom: '24px' }}>
                        <h3 style={{ 
                          fontSize: '18px', 
                          fontWeight: '600', 
                          marginBottom: '4px',
                          color: colors.textPrimary 
                        }}>
                          Version {release.version} {release.isLatest && '- Latest'}
                        </h3>
                        {release.title && (
                          <h4 style={{ 
                            fontSize: '16px', 
                            fontWeight: '500', 
                            marginBottom: '8px',
                            color: colors.textSecondary,
                            fontStyle: 'italic'
                          }}>
                            {release.title}
                          </h4>
                        )}
                        <p style={{ 
                          fontSize: '12px', 
                          color: colors.textSecondary, 
                          marginBottom: '12px' 
                        }}>
                          Released on {new Date(release.date).toLocaleDateString()}
                        </p>
                        
                        {release.features && release.features.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '6px', color: colors.textPrimary }}>
                              ✨ New Features
                            </h4>
                            <ul style={{ 
                              paddingLeft: '20px', 
                              margin: 0,
                              color: colors.textPrimary 
                            }}>
                              {release.features.map((feature: string, i: number) => (
                                <li key={i}>{feature}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {release.bugFixes && release.bugFixes.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '6px', color: colors.textPrimary }}>
                              🐛 Bug Fixes
                            </h4>
                            <ul style={{ 
                              paddingLeft: '20px', 
                              margin: 0,
                              color: colors.textPrimary 
                            }}>
                              {release.bugFixes.map((fix: string, i: number) => (
                                <li key={i}>{fix}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {release.breaking && release.breaking.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '6px', color: '#ef4444' }}>
                              ⚠️ Breaking Changes
                            </h4>
                            <ul style={{ 
                              paddingLeft: '20px', 
                              margin: 0,
                              color: colors.textPrimary 
                            }}>
                              {release.breaking.map((change: string, i: number) => (
                                <li key={i}>{change}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: colors.surfaceElevated, 
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: colors.textSecondary 
                    }}>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', color: colors.textSecondary }}>
                    Loading release notes...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Personalization Modal */}
      <PersonalizationModal
        isOpen={isPersonalizationOpen}
        onClose={() => setIsPersonalizationOpen(false)}
        currentPersona={persona}
        onPersonaChange={handlePersonaChange}
      />
      </div>
    </>
  );
};

export default Header;